import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

import { festivalsAPI, handleApiError } from '../services/api';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024; // 5MB — must match backend MAX_FESTIVAL_BANNER_SIZE.

interface HeroImageDropzoneProps {
  /** Festival id — required to upload. In create mode (no id yet) the
   * dropzone renders a disabled "Save festival first" hint. */
  festivalId?: string | null;
  /** Currently saved banner URL — used to render the preview. */
  currentUrl?: string | null;
  /** Called with the new absolute URL after a successful upload. */
  onUploaded: (newUrl: string) => void;
  disabled?: boolean;
}

/**
 * Drag-and-drop (or click-to-pick) banner uploader for festivals.
 *
 * Posts to `POST /admin/festivals/{id}/hero` and lifts the resulting URL up
 * via `onUploaded`. Backend enforces the canonical content-type + size rules;
 * client-side checks only exist to avoid wasted round-trips for obvious
 * mistakes.
 */
export const HeroImageDropzone: React.FC<HeroImageDropzoneProps> = ({
  festivalId,
  currentUrl,
  onUploaded,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const canUpload = !disabled && !!festivalId && !isUploading;

  const validate = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
      return 'Поддържат се само JPEG, PNG и WebP.';
    }
    if (file.size > MAX_BYTES) {
      return `Файлът надвишава ${MAX_BYTES / (1024 * 1024)}MB.`;
    }
    if (file.size === 0) {
      return 'Файлът е празен.';
    }
    return null;
  };

  const upload = useCallback(
    async (file: File) => {
      if (!festivalId) return;
      setError(null);
      const clientError = validate(file);
      if (clientError) {
        setError(clientError);
        return;
      }
      setIsUploading(true);
      try {
        const res = await festivalsAPI.uploadFestivalHero(festivalId, file);
        const newUrl = res.data.hero_image_url;
        if (newUrl) onUploaded(newUrl);
      } catch (err) {
        setError(handleApiError(err));
      } finally {
        setIsUploading(false);
      }
    },
    [festivalId, onUploaded]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (!canUpload) return;
      const file = e.dataTransfer.files?.[0];
      if (file) upload(file);
    },
    [canUpload, upload]
  );

  const handleSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) upload(file);
      // Reset input so picking the same file twice still fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    },
    [upload]
  );

  return (
    <div className="hero-dropzone">
      <div
        className={`hero-dropzone-area${isDragging ? ' dragging' : ''}${
          !canUpload ? ' disabled' : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (canUpload) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => canUpload && inputRef.current?.click()}
        role="button"
        tabIndex={canUpload ? 0 : -1}
        aria-disabled={!canUpload}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Festival banner preview"
            className="hero-dropzone-preview"
          />
        ) : (
          <div className="hero-dropzone-empty">
            <strong>Пусни изображение тук</strong>
            <span>или кликни за избор</span>
          </div>
        )}

        {isUploading && (
          <div className="hero-dropzone-overlay">Качване...</div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleSelect}
        disabled={!canUpload}
        style={{ display: 'none' }}
      />

      <div className="hero-dropzone-hint">
        {!festivalId ? (
          <span>Запази фестивала първо, после качи банер.</span>
        ) : (
          <span>
            JPEG / PNG / WebP, до 5MB. Препоръчителен размер{' '}
            <strong>1600×800</strong> (2:1).
          </span>
        )}
      </div>

      {error && <div className="field-error">{error}</div>}
    </div>
  );
};
